namespace Solovis.UI {

    /*
    * InputControl is a UI element for selection, date, utcTimestamp, text, or numeric input.
    * 
    * options:
    * type: "select", "text", "number", "date", "password", or "utcTimestamp" //TODO consider adding other HTML input types
    * width: width of the control
    * readonly: disallow user input but still allow value to post
    * max: max value for number, max date for date, max date and time for utcTimestamp, max length for text.
    * min: min value for number, min date for date, min date and time for utcTimestamp, min length for text.
    * placeholder: hint displayed when empty
    * required: whether or not the input is required.
    * value: initial value
    * display: whether or not to display the input control and/or validation messages
    * {
    *   showInput: true,
    *   showValidation: true
    * }
    * validate: custom validation function.  must return an object with the following two properties:
    * {
    *   isValid: true,
        message: null
    * }
    * 
    * validationModel: custom validation model.  It is assumed that if this is given, that validation messages 
    * will be displayed elsewhere, so display.showValidation will be set to false.
    * 
    * change: custom change function
    * enterKey: callback on enter key keyup.  Will only fire if input is valid
    * escapeKey: callback on esc key keyup
    * tabKey: callback on tab key keydown.  Will only fire if input is valid
    * shiftTabkey: callback on shift+tab key keydown.  Will only fire if input is valid
    * onInvalid: callback when validation fails.
    * onValid: callback when validation succeeds.
    * 
    * fireValueChangedOnKeyup: trigger valueChanged on keyup
    * 
    * date specific options:
    *   monthEnd: only show month/year in the calendar dropdown, and initial value/ returned value is always the last day of the month
    * 
    * number specific options:
    *   decimals: number of decimal places to allow.  null for no limit.
    *   showPercentSign: display a percent sign in the input box
    *   treatBlankAsZero: return "0" if the value is null
    * 
    * utcTimestamp specific options:
    *   utcDisplayMode: "local"/"utc" (default: utc), determines whether time is shown as local or UTC time.
    * 
    * select specific options:
    *   data: 
    *   [
    *   {text: "sometext", value: "somevalue"}
    *   ]
    * 
    */

    export interface InputControlOptions {
        type: string;
        container: JQuery;
        validationModel?: Solovis.UI.InputControls.ValidationModel;

        width?: any;
        readonly?: boolean;
        placeholder?: string;
        required?: boolean;

        value?: any;

        min?: any;
        max?: any;
        display?: any;

        monthEnd?: boolean;
        treatBlankAsZero?: boolean;
        data?: any;

        utcDisplayMode?: "utc" | "local";
        decimals?: number;
        showPercentSign?: boolean;

        validate?: any;
        change?: any;
        enterKey?: any;
        escapeKey?: any;
        tabKey?: any;
        shiftTabkey?: any;
        onInvalid?: any;
        onValid?: any;

        fireValueChangedOnKeyup?: boolean;
    }

    export class InputControl {

        public _type: string;
        public _isDirty: boolean;
        public _isValid: boolean;
        public _dateTimeFormat: string;
        public _container: any;
        public _width: any;
        public _readonly: boolean;
        public _placeholder: string;
        public _required: boolean;
        public _value: any;
        public _max: any;
        public _min: any;
        public _display: any;
        public _validationModel: Solovis.UI.InputControls.ValidationModel;
        public _monthEnd: boolean;
        public _dateFormat: string;

        public _input: JQuery;

        public _decimals: number;
        public _showPercentSign: boolean;
        public _treatBlankAsZero: boolean;
        
        public _utcDisplayMode: string;      
        public _data: any;

        public _validate: any;
        public _change: any;
        public _enterKey: any;
        public _escapeKey: any;
        public _tabKey: any;
        public _shiftTabkey: any;
        public _onInvalid: any;
        public _onValid: any;

        public _fireValueChangedOnKeyup: boolean;

        private _datePicker: any;
        private dropDownList: any;

        private _overwrite: any;

        private _validationReason: JQuery;


        constructor(options: InputControlOptions) {
            if (!options.type) {
                throw "Type is required.";
            }
            if (!options.container) {
                throw "Container is required.";
            }

            this._type = options.type;
            this._isDirty = false;
            this._isValid = true;
            this._dateTimeFormat = "MMM d, yyyy h:mm:ss tt";

            // Base options
            this._container = options.container;
            this._width = options.width ? options.width : null;
            this._readonly = options.readonly ? options.readonly : null;
            this._placeholder = options.placeholder ? options.placeholder : null;
            this._required = options.required ? options.required : false;
            this._value = Solovis.isNotNull(options.value) ? options.value : null;
            this._max = Solovis.isNotNull(options.max) ? options.max : null;
            this._min = Solovis.isNotNull(options.min) ? options.min : null;
            this._display = options.display ? options.display : null;

            this._validationModel = options.validationModel ? options.validationModel : null;
            if (this._validationModel) {
                if (!this._display) {
                    this._display = {};
                }
                this._display.showValidation = false;
            }

            if (this._type === "date" || this._type === "utcTimestamp") {
                if (this._max) {
                    if (isNaN(new Date(this._max).valueOf())) {
                        throw "Invalid max date given.";
                    }
                }
                else {
                    this._max = new Date("12/31/2149");
                }
                if (this._min) {
                    if (isNaN(new Date(this._min).valueOf())) {
                        throw "Invalid min date given.";
                    }
                }
                else {
                    this._min = new Date("1/1/1900");
                }
            }

            // Date specific options
            this._monthEnd = options.monthEnd ? options.monthEnd : false;
            this._dateFormat = this._monthEnd ? "MMM yyyy" : "MMM d, yyyy";

            // Number specific options
            this._decimals = Solovis.isNotNull(options.decimals) ? options.decimals : null;
            this._showPercentSign = options.showPercentSign ? options.showPercentSign : false;
            this._treatBlankAsZero = options.treatBlankAsZero ? options.treatBlankAsZero : false;

            // utcTimestamp specific options
            this._utcDisplayMode = Solovis.isNotNull(options.utcDisplayMode) ? options.utcDisplayMode : "utc";

            // Select specific options
            if (this._type === "select" && _.isEmpty(options.data)) {
                throw "Data is required for select input type.";
            }
            this._data = options.data ? options.data : null;

            // Events
            this._validate = options.validate ? options.validate : null;
            this._change = options.change ? options.change : null;
            this._enterKey = options.enterKey ? options.enterKey : null;
            this._escapeKey = options.escapeKey ? options.escapeKey : null;
            this._tabKey = options.tabKey ? options.tabKey : null;
            this._shiftTabkey = options.shiftTabkey ? options.shiftTabkey : null;
            this._onInvalid = options.onInvalid ? options.onInvalid : null;
            this._onValid = options.onValid ? options.onValid : null;

            this._fireValueChangedOnKeyup = options.fireValueChangedOnKeyup ? true : false;

            this._render();
        }


        public static create(options: InputControlOptions) {
            // TODO: This can be enhanced later to return different implementations of InputControl depending
            //   on the type requested. However, all InputControl implementations should follow common conventions
            //   for events and getters/setters.
            var inputControl = new Solovis.UI.InputControl(options);
            return inputControl;
        }

        public _render() {
            var self = this;

            this._container.addClass("solovis-input");

            if (this._container.hasClass("formbuilder-entry-container")) {
                // We specially handle the case of the InputControl appearing directly
                // inside the form builder entry container by removing the padding. If the
                // InputControl is nested deeper within other elements, the consumer of
                // FormBuilder and InputControl is responsible for further styling.
                this._container
                    .css("padding-top", "0")
                    .css("padding-bottom", "0");
            }

            this._input = $("<input>")
                .addClass("k-textbox")
                .css("cursor", this._readonly ? "not-allowed" : null)
                .appendTo(this._container)
                .val(this._value)
                .attr("type", function () {
                    switch (self._type) {
                        case "date":
                            return "date";
                        case "select":
                            return null;
                        case "password":
                            return "password";
                        default:
                            return "text";
                    }
                })
                .attr("readonly", this._readonly ? "" : null)
                .css("display", function () {
                    if (self._display) {
                        if (self._display.showInput === false) {
                            return "none";
                        }
                    }
                    return null;
                })
                .keyup(function (e) {
                    var key = e.keyCode || e.which;

                    // The calendar dropdown in a kendo datepicker intercepts the enter key.
                    // So if we are entering a date instead of picking one from the calendar,
                    // close the calendar so it doesn't get in the way
                    if (self._type === "date" && self._datePicker) {
                        self._datePicker.close();
                    }

                    if (key === 13 && self._enterKey) {
                        // Enter

                        e.preventDefault();
                        if (self.doValidate()) {
                            self._enterKey();
                        }
                    }
                    else if (key === 27 && self._escapeKey) {
                        // Escape

                        e.preventDefault();
                        self._escapeKey();
                    }

                    if (self._fireValueChangedOnKeyup) {
                        self.notifyValueChanged();
                    }
                })
                .keydown(function (e) {
                    var key = e.keyCode || e.which;

                    if (key === 9 && e.shiftKey && self._shiftTabkey) {
                        e.preventDefault();
                        // Shift Tab

                        if (self.doValidate()) {
                            self._shiftTabkey();
                        }
                    }
                    else if (key === 9 && self._tabKey) {
                        e.preventDefault();
                        // Tab

                        if (self.doValidate()) {
                            self._tabKey();
                        }
                    }
                })
                .keypress(function (e) {
                    self._isDirty = true;
                    if (self._type === "number") {
                        var key = e.keyCode || e.which;
                        var keyString = String.fromCharCode(key);
                        if (!$.isNumeric(String.fromCharCode(key))
                            && keyString !== ","
                            && keyString !== "-"
                            && keyString !== "."
                            && e.key !== "Backspace") {
                            if ((window as any).MozApplicationEvent && e.charCode !== 0 && e.which !== 0) {
                                // FireFox counts backspace, arrows, home, end as a keypress.  
                                // FireFox also puts a charCode of 46 for del
                                self._flashWarning();
                                e.preventDefault();
                            }
                            else if (!(window as any).MozApplicationEvent) {
                                self._flashWarning();
                                e.preventDefault();
                            }

                        }
                        if (self._input.val().indexOf(".") !== -1 && String.fromCharCode(key) === ".") {
                            self._flashWarning();
                            e.preventDefault();
                        }
                        if (self._input.val()[self._input.val().length - 1] === "," && (String.fromCharCode(key) === "," || String.fromCharCode(key) === ".")) {
                            self._flashWarning();
                            e.preventDefault();
                        }
                    }
                })
                .blur(function () {
                    var val = self._input.val();
                    if (self._isDirty) {
                        self.doValidate();
                    }
                })
                .change(function () {
                    if (self._type !== "date" && self._type !== "select") {
                        self.notifyValueChanged();
                    }
                });

            if (Solovis.isNotNull(this._width)) {
                if ($.isNumeric(this._width)) {
                    this._input.width(this._width);
                }
                else if (this._width.indexOf("%") !== -1 ||
                    this._width.indexOf("px") !== -1 ||
                    this._width.indexOf("em") !== -1) {
                    this._input.css("width", this._width);
                }
            }

            if (this._type === "select") {
                this._input.removeClass("k-textbox");
                this.dropDownList =
                    this._input.kendoDropDownList({
                        dataTextField: "text",
                        dataValueField: "value",
                        dataSource: this._data,
                        value: this._value,
                        change: function () {
                            self.notifyValueChanged();
                        },
                        template: '# if (data.disabled) { #' +
                                  '<span class="k-state-disabled">#= text #</span>' +
                                  '# } else { #' +
                                  '<span>#= text #</span>' +
                                  '# } #',
                        valueTemplate: '<span>#= text #</span>',
                        select: function (e) {
                            const dataItem = this.dataItem(e.item.index());
                            if (dataItem.disabled) {
                                    e.preventDefault();
                            }
                        }
                    }).data("kendoDropDownList");

                // This prevents the select control from cutting off text for long selected strings.
                var defaultMaxStringWidth = 122; // The max length the dropdownlist can display without cutting off text
                var maxDataStringWidth = 0;
                var padding = 55;
                var temp = $("<div>")
                    .css("position", "absolute")
                    .css("visibility", "hidden")
                    .css("left", "-5000px")
                    .css("top", "-5000px")
                    .css("white-space", "nowrap")
                    .appendTo("body");


                $(this._data).each(function (index, dataItem: any) {
                    temp.text(dataItem.text);
                    var dataTextWidth = temp.width();
                    if (dataTextWidth > maxDataStringWidth) {
                        maxDataStringWidth = dataTextWidth;
                    }

                });
                if (maxDataStringWidth > defaultMaxStringWidth) {
                    var widget = this._input.closest(".k-widget");
                    var newWidth = maxDataStringWidth + padding;
                    widget.width(newWidth);
                }

                temp.remove();

            }

            if (this._type === "date") {

                this._value = this._convertDateValueToDate(this._value);

                this._input.removeClass("k-textbox");
                this._datePicker =
                    this._input.kendoDatePicker({
                        start: self._monthEnd ? "year" : "month",
                        depth: self._monthEnd ? "year" : "month",

                        change: function () {
                            self._overwrite = false;
                            if (self.doValidate()) {
                                self._isDirty = true;
                                self.notifyValueChanged();
                            }
                        },
                        format: this._dateFormat,
                        parseFormats: [
                            "MM-dd-yyyy", "MM/dd/yyyy", "dd MMM yyyy", "MMM dd yyyy", "MMMM dd yyyy", "dd MMMM yyyy",
                            "MM-dd-yy", "dd MMM yy", "MMM dd yy", "MMMM dd yy", "dd MMMM yy", "MM/dd/yy",
                            "yyyy-MM-dd",
                            "D", "d"
                        ],
                        max: this._max,
                        min: this._min,
                        value: this._value
                    })
                        .data("kendoDatePicker");

            }

            if (this._type === "utcTimestamp") {
                this._input.removeClass("k-textbox");

                var utcDate = this._convertUtcTimestampToDate(this._value);

                this._datePicker =
                    this._input.kendoDateTimePicker({
                        change: function () {
                            if (self.doValidate()) {
                                self._isDirty = true;
                                self.notifyValueChanged();
                            }
                        },
                        format: this._dateTimeFormat,
                        parseFormats: [

                            // 12 hour time value given
                            "MM-dd-yyyy hh:mm tt", "MM/dd/yyyy hh:mm tt", "dd MMM yyyy hh:mm tt", "MMM dd yyyy hh:mm tt", "MMMM dd yyyy hh:mm tt", "dd MMMM yyyy hh:mm tt",
                            "MM-dd-yy hh:mm tt", "dd MMM yy hh:mm tt", "MMM dd yy hh:mm tt", "MMMM dd yy hh:mm tt", "dd MMMM yy hh:mm tt", "MM/dd/yy hh:mm tt",
                            "yyyy-MM-dd hh:mm tt",

                            // 12 hour time value given, with seconds
                            "MM-dd-yyyy hh:mm:ss tt", "MM/dd/yyyy hh:mm:ss tt", "dd MMM yyyy hh:mm:ss tt", "MMM dd yyyy hh:mm:ss tt", "MMMM dd yyyy hh:mm:ss tt", "dd MMMM yyyy hh:mm:ss tt",
                            "MM-dd-yy hh:mm:ss tt", "dd MMM yy hh:mm:ss tt", "MMM dd yy hh:mm:ss tt", "MMMM dd yy hh:mm:ss tt", "dd MMMM yy hh:mm:ss tt", "MM/dd/yy hh:mm:ss tt",
                            "yyyy-MM-dd hh:mm:ss tt",

                            // 24 hour time value given
                            "MM-dd-yyyy HH:mm", "MM/dd/yyyy HH:mm", "dd MMM yyyy HH:mm", "MMM dd yyyy HH:mm", "MMMM dd yyyy HH:mm", "dd MMMM yyyy HH:mm",
                            "MM-dd-yy HH:mm", "dd MMM yy HH:mm", "MMM dd yy HH:mm", "MMMM dd yy HH:mm", "dd MMMM yy HH:mm", "MM/dd/yy HH:mm",
                            "yyyy-MM-dd HH:mm",

                            // 24 hour time value given, with seconds
                            "MM-dd-yyyy HH:mm:ss", "MM/dd/yyyy HH:mm:ss", "dd MMM yyyy HH:mm:ss", "MMM dd yyyy HH:mm:ss", "MMMM dd yyyy HH:mm:ss", "dd MMMM yyyy HH:mm:ss",
                            "MM-dd-yy HH:mm:ss", "dd MMM yy HH:mm:ss", "MMM dd yy HH:mm:ss", "MMMM dd yy HH:mm:ss", "dd MMMM yy HH:mm:ss", "MM/dd/yy HH:mm:ss",
                            "yyyy-MM-dd HH:mm:ss",

                            // No time value given
                            "MM-dd-yyyy", "MM/dd/yyyy", "dd MMM yyyy", "MMM dd yyyy", "MMMM dd yyyy", "dd MMMM yyyy",
                            "MM-dd-yy", "dd MMM yy", "MMM dd yy", "MMMM dd yy", "dd MMMM yy", "MM/dd/yy",
                            "yyyy-MM-dd",

                            "D", "d",

                            // M/d/yyyy h:mm tt is equivalent of g (not supported by jquery globalization).
                            "M/d/yyyy h:mm tt"
                        ],

                        max: this._max,
                        min: this._min,
                        value: utcDate
                    })
                        .data("kendoDateTimePicker");
            }

            if (this._showPercentSign) {
                $("<span>")
                    .addClass("solovis-input-overlay")
                    .css("right", "18px")
                    .css("top", "2px")
                    .text("%")
                    .appendTo(this._container);
            }
        }

        public _convertDateValueToDate(incomingDateValue) {
            var date = null;
            if (incomingDateValue) {

                // Ensure that this._value is a date as opposed to a date string
                date = new Date(incomingDateValue);

                if (this._monthEnd) {
                    var monthEndDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                    date = monthEndDate;
                }
            }

            return date;
        }

        public _validateDate() {
            var self = this;

            var showValid = function () {
                var val = self._datePicker.value();

                self._input.closest(".k-picker-wrap").removeClass("has-error");
                if (self._type === "date" && val) {
                    self._input.val(kendo.toString(val, self._dateFormat));
                }
                else if (self._type === "utcTimestamp" && val) {
                    self._input.val(kendo.toString(val, self._dateTimeFormat));
                }
                self._showSuccess();
            }

            if (this._input.val() === "" && !this._required) {
                showValid();
                return true;
            }

            var date = null;

            if (this._datePicker) {
                date = this._datePicker.value();
            }

            if (this._type === "utcTimestamp") {
                date = Solovis.Time.getUtcDateFromLocalDate(date);
            }

            if (Solovis.isNotNull(date)) {
                if (isNaN(date.valueOf())) {
                    this._input.closest(".k-picker-wrap").removeClass("has-success");
                    this._input.closest(".k-picker-wrap").addClass("has-error");
                    this._showError("Invalid date.");
                    return false;
                }
                else if (this._max && date > this._max) {
                    this._showError("Date must be on or before " + kendo.toString(this._max, this._dateFormat));
                    return false;
                }
                else if (this._min && date < this._min) {
                    this._showError("Date must be on or after " + kendo.toString(this._min, this._dateFormat));
                    return false;
                }
            }
            showValid();
            return true;
        }

        public value(value?) {
            if (arguments.length) {
                this.setValue(value);
            }
            return this.getValue();
        }

        public getValue() {
            switch (this._type) {
                case "number":
                    var val = this._input.val().replace(/,/g, "");
                    if (val === "") {
                        if (this._treatBlankAsZero) {
                            val = "0";
                        }
                        else {
                            val = null;
                        }
                    }
                    return val;

                case "date":
                    var date = this._datePicker.value();

                    if (this._monthEnd && date) {

                        var monthEndDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                        date = monthEndDate;
                    }
                    return date;

                case "utcTimestamp":
                    var effectiveDateValue = this._datePicker.value();
                    var utcTimestampValue = this._convertDateToUtcTimestamp(effectiveDateValue);
                    return utcTimestampValue;

                default:
                    return this._input.val();
            }
        };

        public _convertUtcTimestampToDate(value) {
            if (Solovis.isNull(value)) {
                return null;
            }

            var dateValue = null;
            if (_.isNumber(value)) {
                dateValue = new Date(value);
            }
            else if (typeof (value) === "object" && value.getTime) {
                dateValue = value;
            }
            else if (Solovis.isNotNull(value)) {
                throw "Expected number or date.";
            }

            if (this._utcDisplayMode == "local") {
                return dateValue;
            }
            else {
                return Solovis.Time.getUtcDateFromLocalDate(dateValue);
            }
        }

        public _convertDateToUtcTimestamp(dateValue) {
            if (dateValue) {
                if (this._utcDisplayMode == "local") {
                    return dateValue.getTime();
                }
                else {
                    return Solovis.Time.getLocalDateFromUtcDate(dateValue).getTime();
                }
            }

            return null;
        }

        public setValue(value) {
            switch (this._type) {
                case "number":
                    this._input.val(value);
                    break;
                case "date":
                    value = this._convertDateValueToDate(value);
                    this._datePicker.value(value);
                    break;
                case "utcTimestamp":
                    {
                        var utcDateTime = this._convertUtcTimestampToDate(value);
                        this._datePicker.value(utcDateTime);
                        break;
                    }
                default:
                    this._input.val(value);
                    break;
            }
            this.doValidate();

            this.notifyValueChanged();
        }

        public doValidate() {
            this._isValid = this._doStandardValidation();
            if (this._isValid && this._validate) {
                var customValidationResults = this._validate();
                this._isValid = customValidationResults.isValid;
                if (!customValidationResults.isValid) {
                    this._showError(customValidationResults.message);
                }
            }
            return this._isValid;
        }

        public _flashWarning() {
            var self = this;

            this._input.removeClass("has-success");
            this._input
                .addClass("has-error");
            setTimeout(function () {
                self._input.removeClass("has-error");
            }, 100);
        }

        public _doStandardValidation() {
            var self = this;
            var val = this._input.val();
            this._isValid = true;
            if (!val) {
                if (this._required) {
                    this._showError("This is a required field.");
                    return false;
                }
            }

            switch (this._type) {
                case "number":
                    if (val === "") {
                        val = "0";
                    }
                    if (!$.isNumeric(val.replace(/,/g, ""))) {
                        self._showError("Field must contain a numeric value.");
                        this._isValid = false;
                    }
                    if (Solovis.isNotNull(this._min) && val < this._min) {
                        this._showError("Field must be greater than or equal to " + this._min);
                        this._isValid = false;
                    }
                    if (Solovis.isNotNull(this._max) && val > this._max) {
                        this._showError("Field must be less than or equal to " + this._max);
                        this._isValid = false;
                    }
                    if (Solovis.isNotNull(this._decimals) && val.indexOf(".") !== -1) {
                        var splits = val.split(".");
                        if (splits[splits.length - 1].length > this._decimals) {
                            this._showError("Only " + this._decimals + " decimal places are allowed for this field");
                            this._isValid = false;
                        }
                    }
                    break;
                case "text":
                    if (Solovis.isNotNull(this._min) && val.length < this._min) {
                        this._showError("Field must contain " + this._min + " or more characters.");
                        this._isValid = false;
                    }
                    if (Solovis.isNotNull(this._max) && val.length > this._max) {
                        this._showError("Field must contain " + this._max + " or less characters.");
                        this._isValid = false;
                    }
                    break;
                case "date":
                case "utcTimestamp":
                    if (!this._validateDate()) {
                        this._isValid = false;
                    }
                    break;
            }

            if (this._isValid) {
                this._showSuccess();
            }
            return this._isValid;
        }

        public isValid(): boolean {
            return this.doValidate();
        }

        public notifyValueChanged() {
            $(this).trigger("valueChanged");
            if (this._change) {
                this._change();
            }
        }

        public _showSuccess() {
            if (!this._display || this._display.showValidation) {
                if (this._validationReason) {
                    this._validationReason.remove();
                    this._validationReason = null;
                }
                if (this._type === "date" || this._type === "utcTimestamp") {
                    this._container.find(".k-widget").css("margin-bottom", "5px");
                    this._container.find(".k-picker-wrap").removeClass("has-error");
                }
                else {
                    this._input.css("margin-bottom", "5px");
                    this._input.removeClass("has-error");
                }
            }
            else if (this._validationModel) {
                this._validationModel.state(null);
            }

            if (this._onValid) {
                this._onValid();
            }
        }

        public _showError(message) {
            var self = this;
            if (!this._display || this._display.showValidation) {
                if (!this._validationReason) {
                    this._validationReason = $("<label>")
                        .css("font-size", "100%")
                        .css("display", "block")
                        .css("border-radius", "0")
                        .css("text-align", "left")
                        .css("white-space", "pre-wrap")
                        .css("word-wrap", "break-word")
                        .css("padding-bottom", "5px")
                        .css("clear", "both")
                        .addClass("label inputcontrol-validation-error")
                        .text(message)
                        .appendTo(this._container);
                    if (this._type === "date" || this._type === "utcTimestamp") {
                        this._container.find(".k-widget").css("margin-bottom", "0");
                        this._container.find(".k-picker-wrap").removeClass("has-success");
                        this._container.find(".k-picker-wrap").addClass("has-error");
                    }
                    else {
                        this._input.css("margin-bottom", "0");
                        this._input.addClass("has-error");
                        this._input.removeClass("has-success");
                    }
                }
                else {
                    this._validationReason.text(message);
                }
            }
            else if (this._validationModel) {
                this._validationModel.setErrorMessage(message);
            }


            if (this._onInvalid) {
                this._onInvalid();
            }
        }


        /**
         * Apply focus to the input control.
         */
        public focus() {
            let inputs = $("input", this._container);
            if (inputs.length > 0) {
                $(inputs[0]).focus();
            }
        }
    }

}


   